import React, { useState, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';

const ImportarAnimalesPage = () => {
  const { user } = useAuth();
  const fileRef = useRef(null);
  const [file, setFile] = useState(null);
  const [step, setStep] = useState('upload');
  const [preview, setPreview] = useState(null);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [sexoDefault, setSexoDefault] = useState('');

  const handleFileSelect = (e) => {
    const f = e.target.files[0];
    if (!f) return;
    const ext = f.name.split('.').pop().toLowerCase();
    if (!['csv', 'xlsx', 'xls'].includes(ext)) {
      setError('Formato no soportado. Usa archivos .csv, .xls o .xlsx');
      return;
    }
    setFile(f);
    setError('');
    setPreview(null);
    setResult(null);
    setStep('upload');
  };

  const handlePreview = async () => {
    if (!file) return;
    setLoading(true);
    setError('');
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('establecimiento_id', user.establecimiento_id);
      if (sexoDefault) formData.append('sexo_default', sexoDefault);
      const res = await api.post('/importar/preview', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setPreview(res.data);
      setStep('preview');
    } catch (err) {
      setError(err.response?.data?.detail || 'Error al previsualizar');
    } finally { setLoading(false); }
  };

  const handleImport = async () => {
    if (!file) return;
    setLoading(true);
    setError('');
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('establecimiento_id', user.establecimiento_id);
      if (sexoDefault) formData.append('sexo_default', sexoDefault);
      const res = await api.post('/importar/ejecutar', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setResult(res.data);
      setStep('result');
    } catch (err) {
      setError(err.response?.data?.detail || 'Error al importar');
    } finally { setLoading(false); }
  };

  const handleDownloadTemplate = async () => {
    setError('');
    try {
      const res = await api.get('/importar/plantilla', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'TKorveX_Plantilla_Animales.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      setError('Error al descargar la plantilla. Verifica tu conexion e intenta de nuevo.');
    }
  };

  const reset = () => {
    setFile(null);
    setPreview(null);
    setResult(null);
    setStep('upload');
    setError('');
    if (fileRef.current) fileRef.current.value = '';
  };

  const totalValid = (preview?.valid || 0) + (preview?.existing_updated || 0);

  return (
    <div className="p-4 sm:p-6 max-w-4xl mx-auto space-y-6" data-testid="importar-page">
      <div>
        <h2 className="text-xl sm:text-2xl font-bold text-gray-900">Importar Animales</h2>
        <p className="text-sm text-gray-500 mt-1">Carga masiva desde archivos CSV o Excel (incluye pesajes)</p>
      </div>

      {/* Step 1: Upload */}
      <div className="bg-white rounded-xl shadow-sm p-6 border">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold text-gray-800">1. Seleccionar archivo</h3>
          <button
            onClick={handleDownloadTemplate}
            className="px-4 py-2 text-sm text-green-700 border border-green-300 rounded-lg hover:bg-green-50 font-medium"
            data-testid="download-template-btn"
          >
            Descargar Plantilla Excel
          </button>
        </div>

        <div
          className="border-2 border-dashed border-gray-300 rounded-xl p-8 text-center hover:border-green-400 transition cursor-pointer"
          onClick={() => fileRef.current?.click()}
          data-testid="file-drop-zone"
        >
          <input
            ref={fileRef}
            type="file"
            accept=".csv,.xlsx,.xls"
            onChange={handleFileSelect}
            className="hidden"
            data-testid="file-input"
          />
          {file ? (
            <div>
              <p className="text-lg font-semibold text-green-700">{file.name}</p>
              <p className="text-sm text-gray-500 mt-1">{(file.size / 1024).toFixed(1)} KB</p>
            </div>
          ) : (
            <div>
              <svg className="mx-auto h-12 w-12 text-gray-400 mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" /></svg>
              <p className="text-gray-600 font-medium">Hace clic para seleccionar un archivo</p>
              <p className="text-sm text-gray-400 mt-1">CSV, XLS o XLSX</p>
            </div>
          )}
        </div>

        {file && step === 'upload' && (
          <div className="mt-4 flex flex-col sm:flex-row sm:items-end justify-between gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Sexo para animales nuevos (si el archivo no lo trae, ej. Tru-Test)</label>
              <select
                value={sexoDefault}
                onChange={(e) => setSexoDefault(e.target.value)}
                className="px-3 py-2 border rounded-lg text-sm bg-white"
                data-testid="sexo-default-select"
              >
                <option value="">Solo pesajes de animales existentes</option>
                <option value="macho">Machos (novillos / toros)</option>
                <option value="hembra">Hembras (vaquillas / vacas)</option>
              </select>
            </div>
            <button
              onClick={handlePreview}
              disabled={loading}
              className="px-6 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium text-sm disabled:opacity-50"
              data-testid="preview-btn"
            >
              {loading ? 'Analizando...' : 'Previsualizar'}
            </button>
          </div>
        )}
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700 text-sm" data-testid="import-error">
          {error}
        </div>
      )}

      {/* Step 2: Preview */}
      {step === 'preview' && preview && (
        <div className="bg-white rounded-xl shadow-sm p-6 border space-y-4">
          <h3 className="font-semibold text-gray-800">2. Previsualizar datos</h3>

          {preview.formato && (
            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 text-sm text-emerald-800" data-testid="formato-detectado">
              <p><span className="font-semibold">Formato detectado:</span> {preview.formato}</p>
              {preview.columnas && Object.keys(preview.columnas).length > 0 && (
                <p className="text-xs mt-1 text-emerald-700">
                  Columnas reconocidas: {Object.entries(preview.columnas).map(([orig, canon]) => `${orig} → ${canon}`).join(', ')}
                </p>
              )}
              {preview.columnas_ignoradas?.length > 0 && (
                <p className="text-xs mt-1 text-emerald-600">Ignoradas: {preview.columnas_ignoradas.join(', ')}</p>
              )}
            </div>
          )}

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div className="bg-gray-50 rounded-lg p-4 text-center">
              <p className="text-2xl font-bold text-gray-800">{preview.total_rows}</p>
              <p className="text-xs text-gray-500">Filas totales</p>
            </div>
            <div className="bg-green-50 rounded-lg p-4 text-center">
              <p className="text-2xl font-bold text-green-700">{preview.valid}</p>
              <p className="text-xs text-green-600">Animales nuevos</p>
            </div>
            <div className="bg-blue-50 rounded-lg p-4 text-center">
              <p className="text-2xl font-bold text-blue-700">{preview.pesajes || 0}</p>
              <p className="text-xs text-blue-600">Pesajes a registrar</p>
            </div>
            <div className={`rounded-lg p-4 text-center ${preview.errors_count > 0 ? 'bg-red-50' : 'bg-gray-50'}`}>
              <p className={`text-2xl font-bold ${preview.errors_count > 0 ? 'text-red-700' : 'text-gray-800'}`}>{preview.errors_count}</p>
              <p className="text-xs text-gray-500">Errores</p>
            </div>
          </div>

          {(preview.existing_updated || 0) > 0 && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-700">
              {preview.existing_updated} animal(es) existente(s) recibiran nuevos pesajes
            </div>
          )}

          {preview.errors?.length > 0 && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 max-h-40 overflow-y-auto">
              <p className="font-semibold text-red-700 text-sm mb-2">Errores encontrados:</p>
              {preview.errors.map((e, i) => (
                <p key={i} className="text-xs text-red-600">{e}</p>
              ))}
            </div>
          )}

          {/* Preview animals table */}
          {preview.preview?.length > 0 && (
            <div className="overflow-x-auto">
              <p className="text-sm font-medium text-gray-700 mb-2">Animales nuevos:</p>
              <table className="min-w-full text-xs" data-testid="preview-table">
                <thead className="bg-gray-50">
                  <tr>
                    {['VID', 'IDE', 'Sexo', 'Raza', 'Categoria', 'F. Nacimiento', 'Color'].map(h => (
                      <th key={h} className="py-2 px-3 text-left font-medium text-gray-500 uppercase">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {preview.preview.map((a, i) => (
                    <tr key={i} className="border-b border-gray-100">
                      <td className="py-2 px-3 font-semibold">{a.VID}</td>
                      <td className="py-2 px-3 text-gray-600">{a.IDE || '--'}</td>
                      <td className="py-2 px-3">
                        <span className={`px-1.5 py-0.5 rounded text-xs ${a.sexo === 'hembra' ? 'bg-pink-100 text-pink-700' : 'bg-blue-100 text-blue-700'}`}>
                          {a.sexo}
                        </span>
                      </td>
                      <td className="py-2 px-3">{a.raza}</td>
                      <td className="py-2 px-3">{a.categoria || '--'}</td>
                      <td className="py-2 px-3">{a.fecha_nacimiento}</td>
                      <td className="py-2 px-3">{a.color || '--'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Preview pesajes table */}
          {preview.preview_pesajes?.length > 0 && (
            <div className="overflow-x-auto">
              <p className="text-sm font-medium text-gray-700 mb-2">Pesajes a registrar:</p>
              <table className="min-w-full text-xs" data-testid="preview-pesajes-table">
                <thead className="bg-blue-50">
                  <tr>
                    {['VID', 'Peso (kg)', 'Fecha Pesaje', 'GMD (kg/dia)'].map(h => (
                      <th key={h} className="py-2 px-3 text-left font-medium text-blue-600 uppercase">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {preview.preview_pesajes.map((p, i) => (
                    <tr key={i} className="border-b border-gray-100">
                      <td className="py-2 px-3 font-semibold">{p.VID}</td>
                      <td className="py-2 px-3">{p.peso} kg</td>
                      <td className="py-2 px-3">{p.fecha}</td>
                      <td className="py-2 px-3">
                        {p.gmd != null ? (
                          <span className={`font-medium ${p.gmd >= 0.5 ? 'text-green-700' : p.gmd >= 0 ? 'text-yellow-600' : 'text-red-600'}`}>
                            {p.gmd.toFixed(3)}
                          </span>
                        ) : '--'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div className="flex justify-between pt-2">
            <button onClick={reset} className="px-4 py-2 text-gray-600 hover:text-gray-800 text-sm" data-testid="reset-btn">
              Cancelar
            </button>
            <button
              onClick={handleImport}
              disabled={loading || (preview.valid === 0 && (preview.pesajes || 0) === 0)}
              className="px-6 py-2.5 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium text-sm disabled:opacity-50"
              data-testid="import-btn"
            >
              {loading ? 'Importando...' : `Importar (${preview.valid} animales, ${preview.pesajes || 0} pesajes)`}
            </button>
          </div>
        </div>
      )}

      {/* Step 3: Result */}
      {step === 'result' && result && (
        <div className="bg-white rounded-xl shadow-sm p-6 border space-y-4" data-testid="import-result">
          <div className={`rounded-lg p-6 text-center ${(result.imported > 0 || result.imported_pesajes > 0) ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
            <p className={`text-2xl font-bold ${(result.imported > 0 || result.imported_pesajes > 0) ? 'text-green-700' : 'text-red-700'}`}>
              {result.message}
            </p>
          </div>

          {result.errors?.length > 0 && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 max-h-40 overflow-y-auto">
              <p className="font-semibold text-red-700 text-sm mb-2">Errores:</p>
              {result.errors.map((e, i) => (
                <p key={i} className="text-xs text-red-600">{e}</p>
              ))}
            </div>
          )}

          <div className="flex justify-center pt-2">
            <button onClick={reset} className="px-6 py-2.5 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium text-sm" data-testid="new-import-btn">
              Nueva Importacion
            </button>
          </div>
        </div>
      )}

      {/* Instructions */}
      <div className="bg-gray-50 rounded-xl p-5 border border-gray-200">
        <h4 className="font-semibold text-gray-700 mb-2">Instrucciones</h4>
        <ul className="text-sm text-gray-600 space-y-1.5 list-disc list-inside">
          <li><strong>Descarga la plantilla</strong> Excel para ver el formato correcto</li>
          <li><strong>Archivos de Tru-Test / Data Link (XR5000, JR5000, ID5000, S3)</strong> se aceptan tal como salen: columnas EID, VID, Weight, Date se reconocen solas</li>
          <li>Campos obligatorios: <strong>VID</strong> (numero visual) y <strong>sexo</strong> (macho/hembra)</li>
          <li>Campo opcional: <strong>peso</strong> (kg) y <strong>fecha_pesaje</strong> — se guardan como historial de pesajes</li>
          <li>Podes cargar <strong>varios pesos del mismo animal</strong> en filas separadas. El sistema calcula la GMD automaticamente</li>
          <li>Si el VID ya existe, no se duplica el animal, solo se agrega el pesaje</li>
          <li>Formatos de fecha aceptados: AAAA-MM-DD, DD/MM/AAAA, DD-MM-AAAA</li>
          <li>Se procesan en lotes de 500 para mantener el rendimiento</li>
          <li>Soporta archivos <strong>.csv</strong>, <strong>.xls</strong> y <strong>.xlsx</strong></li>
        </ul>
      </div>
    </div>
  );
};

export default ImportarAnimalesPage;
